import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ProductSettingsDTO } from '@modeso/types__dgoods-products';
import { Store } from '@ngrx/store';
import { filter, Subject, takeUntil, tap } from 'rxjs';
import { ProductSettingsList } from '../../../../../dgoods-lib-products-fe/src/lib';
import { UserRoles } from '../../shared/enum/userrole.enum';
import { LocalStorageService } from '../../shared/services/localStorage.service';
import { PermissionHelper } from '../../shared/util/permission.helper';
import {
    clearRequestState,
    getProductsSettings,
    updateProductSettings,
} from '../../state/products/actions/products.actions';
import { selectProductsSettings } from '../../state/products/selectors/product-settings.selectors';
import { selectProductsError, selectProductsSuccessState } from '../../state/products/selectors/products.selectors';
import { SettingsEditDialogComponent } from './confirmation-dialog.component';

@Component({
    selector: 'app-product-settings',
    templateUrl: './productSettings.page.html',
    styleUrls: ['./productSettings.page.scss'],
})
export class ProductSettingsPage implements OnInit, OnDestroy {
    readonly displayedColumns: readonly string[] = [
        'productLineName',
        'whiteListed',
        'couponsEnabled',
        'displayOnIOS',
        'displayOnAndroid',
        'isGreyedOut',
        'serviceFee',
        'Action',
    ];
    productSettings: ProductSettingsDTO[] = [];

    readonly rolePermission: boolean;
    private readonly destroy$ = new Subject<void>();

    constructor(
        private readonly localStorageService: LocalStorageService,
        private readonly store: Store,
        private readonly dialog: MatDialog,
    ) {
        this.rolePermission = this.hasPermission();
    }

    public ngOnInit() {
        this.getAllProductSettings();
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    displayDialog(product: ProductSettingsList): void {
        const error$ = this.store.select(selectProductsError);
        const success$ = this.store.select(selectProductsSuccessState);
        // to reset error, success states
        this.store.dispatch(clearRequestState());

        const dialogRef = this.dialog.open(SettingsEditDialogComponent, {
            data: {
                ...product,
                error: error$,
                success: success$,
                updateProductSettings: (productsSettings: ProductSettingsDTO): void =>
                    this.store.dispatch(updateProductSettings({ productsSettings })),
            },
            width: '70%',
            restoreFocus: false,
            disableClose: true,
        });

        dialogRef.afterClosed().pipe(tap(() => window.scrollTo({ top: 0, behavior: 'smooth' })));
    }

    private getAllProductSettings(): void {
        this.store.dispatch(getProductsSettings());
        this.store
            .select(selectProductsSettings)
            .pipe(
                filter((productSettings: ProductSettingsDTO[]) => productSettings != null),
                takeUntil(this.destroy$),
            )
            .subscribe((productSettings: ProductSettingsDTO[]) => {
                this.productSettings = [...productSettings];
                this.productSettings.sort((a, b) => a.productLineName.localeCompare(b.productLineName, 'en-us'));
            });
    }

    private hasPermission(): boolean {
        const role = this.localStorageService.getUserRole();
        const privilegedRoles = [UserRoles.DIGITAL_VAUCHERS_ADMIN, UserRoles.DIGITAL_VAUCHERS_PRODUCT_MANAGER];
        return PermissionHelper.hasPermission(role, privilegedRoles);
    }
}
